/**
 * MyFolder — per-user file storage window.
 *
 *   Backend endpoints:
 *     GET    /api/files          list
 *     POST   /api/files          multipart upload (field `file`)
 *     GET    /api/files/{id}     stream (Range-capable)
 *     DELETE /api/files/{id}     delete
 *
 *   Layout mirrors the Lottel folder chrome (`.com__*`) so it feels native
 *   inside the XP shell.
 *
 *   Double-click behavior per mime:
 *     image/*       → ImageViewer (single image)
 *     text/plain    → Notepad with `src`
 *     application/pdf → new tab
 *     audio/*, video/*, everything else → new tab (browser handles)
 */

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';

import { WindowDropDowns } from 'components';
import { useAuth } from 'auth/AuthContext';
import { api, ApiError } from 'api/client';

import ImageViewer from '../ImageViewer';
import Notepad from '../Notepad';

import dropDownData from './dropDownData';
import go from 'assets/windowsIcons/290.png';
import back from 'assets/windowsIcons/back.png';
import forward from 'assets/windowsIcons/forward.png';
import up from 'assets/windowsIcons/up.png';
import search from 'assets/windowsIcons/299(32x32).png';
import folderOpen from 'assets/windowsIcons/337(32x32).png';
import folderSmall from 'assets/windowsIcons/318(16x16).png';
import documentSmall from 'assets/windowsIcons/308(16x16).png';
import networkSmall from 'assets/windowsIcons/693(16x16).png';
import menu from 'assets/windowsIcons/358(32x32).png';
import dropdown from 'assets/windowsIcons/dropdown.png';
import pullup from 'assets/windowsIcons/pullup.png';
import windows from 'assets/windowsIcons/windows.png';
import folderIcon from 'assets/windowsIcons/folder.png';
import defaultFileIcon from 'assets/windowsIcons/308(32x32).png';
import textFileIcon from 'assets/windowsIcons/327(32x32).png';
import refreshIcon from 'assets/windowsIcons/refresh.png';

const viewerIcon = '/my-art/Ditto.png';

// 20 MB per file (matches server LOTTEL_FILE_MAX_BYTES).
const UPLOAD_MAX_BYTES = 20 * 1024 * 1024;

function iconForMime(mime) {
  if (typeof mime !== 'string') return defaultFileIcon;
  if (mime === 'text/plain') return textFileIcon;
  return defaultFileIcon;
}

function formatSize(bytes) {
  if (!Number.isFinite(bytes) || bytes < 0) return '';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

function formatTotal(bytes) {
  const mb = bytes / (1024 * 1024);
  return mb.toFixed(mb >= 10 ? 0 : 1) + ' MB';
}

function formatUploaded(secs) {
  if (!Number.isFinite(secs)) return '';
  return new Date(secs * 1000).toLocaleString();
}

function fetchList() {
  return api.get('files');
}

function MyFolder({ onClose }) {
  const { user } = useAuth();
  const [files, setFiles] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  const fileInputRef = useRef(null);
  const contentRef = useRef(null);

  const refresh = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setError(null);
    try {
      const data = await fetchList();
      const list = Array.isArray(data && data.files) ? data.files : [];
      setFiles(list);
      // Keep selection if the file is still there.
      setSelectedId(prev => (list.some(f => f.id === prev) ? prev : null));
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const selected = useMemo(() => files.find(f => f.id === selectedId) || null, [
    files,
    selectedId,
  ]);

  const totalBytes = useMemo(
    () => files.reduce((sum, f) => sum + (f.size_bytes || 0), 0),
    [files],
  );

  const uploadFiles = useCallback(
    async list => {
      if (!user || !list || !list.length) return;
      setError(null);
      setNotice(null);
      setBusy(true);
      let uploaded = 0;
      try {
        for (const file of list) {
          if (file.size > UPLOAD_MAX_BYTES) {
            throw new ApiError(
              'file_too_large',
              file.name + ' is over 20 MB',
              413,
            );
          }
          const form = new FormData();
          form.append('file', file, file.name);
          await api.post('files', form);
          uploaded += 1;
        }
        setNotice(
          uploaded === 1
            ? 'Uploaded 1 file.'
            : 'Uploaded ' + uploaded + ' files.',
        );
        await refresh();
      } catch (e) {
        setError(errorMessage(e));
      } finally {
        setBusy(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
    },
    [user, refresh],
  );

  function onPick(e) {
    const list = e.target.files ? Array.from(e.target.files) : [];
    uploadFiles(list);
  }

  function onUploadClick() {
    if (fileInputRef.current) fileInputRef.current.click();
  }

  function openFile(file) {
    if (!file) return;
    const mime = file.mime || '';
    const url = file.url || '/api/files/' + file.id;
    if (mime.startsWith('image/')) {
      openWindow({
        appKey: 'ImageViewer',
        title: file.display_name,
        icon: viewerIcon,
        component: ImageViewer,
        defaultSize: { width: 720, height: 560 },
        resizable: true,
        multiInstance: true,
        injectProps: { images: [url], initialIndex: 0 },
      });
    } else if (mime === 'text/plain') {
      openWindow({
        appKey: 'Notepad',
        title: file.display_name,
        icon: textFileIcon,
        component: Notepad,
        defaultSize: { width: 500, height: 440 },
        resizable: true,
        multiInstance: true,
        injectProps: { src: url, title: file.display_name },
      });
    } else {
      // Fall back to the browser: images/PDF/audio/video all work inline.
      window.open(url, '_blank', 'noopener');
    }
  }

  function openWindow({
    appKey,
    title,
    icon,
    component,
    defaultSize,
    resizable,
    multiInstance,
    injectProps,
  }) {
    window.dispatchEvent(
      new CustomEvent('lottel:dynamic', {
        detail: {
          header: {
            title: title || appKey,
            icon: icon,
            buttons: ['close'],
          },
          component,
          defaultSize: defaultSize || { width: 500, height: 400 },
          defaultOffset: {
            x: Math.max(
              20,
              window.innerWidth / 2 -
                Math.floor((defaultSize && defaultSize.width) || 500) / 2,
            ),
            y: Math.max(
              20,
              window.innerHeight / 2 -
                Math.floor((defaultSize && defaultSize.height) || 400) / 2,
            ),
          },
          resizable: resizable !== false,
          minimized: false,
          maximized: false,
          multiInstance: multiInstance !== false,
          injectProps: injectProps || {},
        },
      }),
    );
  }

  async function deleteSelected() {
    if (!selected || busy) return;
    // eslint-disable-next-line no-alert
    if (!window.confirm('Delete "' + selected.display_name + '"?')) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await api.del('files/' + selected.id);
      await refresh();
      setNotice('Deleted.');
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  function onDrop(e) {
    e.preventDefault();
    setDragOver(false);
    if (!user || busy) return;
    const list = e.dataTransfer.files ? Array.from(e.dataTransfer.files) : [];
    if (list.length) uploadFiles(list);
  }

  function onDragOver(e) {
    e.preventDefault();
    if (!user) return;
    setDragOver(true);
  }

  function onDragLeave(e) {
    if (e.currentTarget === e.target) setDragOver(false);
  }

  function onClickOptionItem(item) {
    if (item === 'Close') onClose();
    else if (item === 'Refresh') refresh();
    else if (item === 'Upload…') onUploadClick();
    else if (item === 'Open') openFile(selected);
    else if (item === 'Delete') deleteSelected();
    else if (item === 'Select All') {
      /* no-op stub */
    }
  }

  if (!user) {
    return (
      <Div>
        <div className="mf__empty">
          <img src={folderOpen} alt="" className="mf__empty__icon" />
          <p>Sign in to open your personal folder.</p>
          <button type="button" onClick={onClose}>
            Close
          </button>
        </div>
      </Div>
    );
  }

  return (
    <Div>
      <section className="com__toolbar">
        <div className="com__options">
          <WindowDropDowns
            items={dropDownData}
            onClickItem={onClickOptionItem}
          />
        </div>
        <img className="com__windows-logo" src={windows} alt="windows" />
      </section>

      <section className="com__function_bar">
        <div className="com__function_bar__button--disable">
          <img className="com__function_bar__icon" src={back} alt="" />
          <span className="com__function_bar__text">Back</span>
          <div className="com__function_bar__arrow" />
        </div>
        <div className="com__function_bar__button--disable">
          <img className="com__function_bar__icon" src={forward} alt="" />
          <div className="com__function_bar__arrow" />
        </div>
        <div className="com__function_bar__button--disable">
          <img className="com__function_bar__icon--normalize" src={up} alt="" />
        </div>
        <div className="com__function_bar__separate" />
        <div
          className="com__function_bar__button"
          onClick={onUploadClick}
          title="Upload files"
        >
          <img
            className="com__function_bar__icon--normalize"
            src={folderOpen}
            alt=""
          />
          <span className="com__function_bar__text">Upload</span>
        </div>
        <div className="com__function_bar__button" onClick={() => refresh()}>
          <img
            className="com__function_bar__icon--normalize"
            src={refreshIcon}
            alt=""
          />
          <span className="com__function_bar__text">Refresh</span>
        </div>
        <div className="com__function_bar__button--disable">
          <img
            className="com__function_bar__icon--normalize"
            src={search}
            alt=""
          />
          <span className="com__function_bar__text">Search</span>
        </div>
        <div className="com__function_bar__separate" />
        <div className="com__function_bar__button">
          <img
            className="com__function_bar__icon--margin12"
            src={menu}
            alt=""
          />
          <div className="com__function_bar__arrow" />
        </div>
      </section>

      <section className="com__address_bar">
        <div className="com__address_bar__title">Address</div>
        <div className="com__address_bar__content">
          <img
            src={folderIcon}
            alt="folder"
            className="com__address_bar__content__img"
          />
          <div className="com__address_bar__content__text">
            {user.username
              ? 'C:\\Users\\' + user.username + '\\My Folder'
              : 'My Folder'}
          </div>
          <img
            src={dropdown}
            alt="dropdown"
            className="com__address_bar__content__img"
          />
        </div>
        <div className="com__address_bar__go">
          <img className="com__address_bar__go__img" src={go} alt="go" />
          <span className="com__address_bar__go__text">Go</span>
        </div>
      </section>

      <div
        className={'com__content' + (dragOver ? ' com__content--dragover' : '')}
        ref={contentRef}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
      >
        <div className="com__content__inner">
          <div className="com__content__left">
            <div className="com__content__left__card">
              <div className="com__content__left__card__header">
                <div className="com__content__left__card__header__text">
                  File Tasks
                </div>
                <img
                  src={pullup}
                  alt=""
                  className="com__content__left__card__header__img"
                />
              </div>
              <div className="com__content__left__card__content">
                <div
                  className="com__content__left__card__row"
                  onClick={onUploadClick}
                >
                  <img
                    className="com__content__left__card__img"
                    src={folderSmall}
                    alt=""
                  />
                  <div className="com__content__left__card__text link">
                    Upload files…
                  </div>
                </div>
                <div
                  className={
                    'com__content__left__card__row' +
                    (selected ? '' : ' mf__row--disabled')
                  }
                  onClick={selected ? () => openFile(selected) : undefined}
                >
                  <img
                    className="com__content__left__card__img"
                    src={documentSmall}
                    alt=""
                  />
                  <div className="com__content__left__card__text link">
                    Open this file
                  </div>
                </div>
                <div
                  className={
                    'com__content__left__card__row' +
                    (selected ? '' : ' mf__row--disabled')
                  }
                  onClick={selected ? deleteSelected : undefined}
                >
                  <img
                    className="com__content__left__card__img"
                    src={networkSmall}
                    alt=""
                  />
                  <div className="com__content__left__card__text link">
                    Delete this file
                  </div>
                </div>
              </div>
            </div>

            <div className="com__content__left__card">
              <div className="com__content__left__card__header">
                <div className="com__content__left__card__header__text">
                  Details
                </div>
                <img
                  src={pullup}
                  alt=""
                  className="com__content__left__card__header__img"
                />
              </div>
              <div className="com__content__left__card__content">
                {selected ? (
                  <>
                    <div className="com__content__left__card__row">
                      <div className="com__content__left__card__text bold black">
                        {selected.display_name}
                      </div>
                    </div>
                    <div className="com__content__left__card__row">
                      <div className="com__content__left__card__text black">
                        {selected.mime}
                      </div>
                    </div>
                    <div className="com__content__left__card__row">
                      <div className="com__content__left__card__text black">
                        {formatSize(selected.size_bytes)}
                      </div>
                    </div>
                    <div className="com__content__left__card__row">
                      <div className="com__content__left__card__text black">
                        {formatUploaded(selected.uploaded_at)}
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="com__content__left__card__row">
                      <div className="com__content__left__card__text bold black">
                        My Folder
                      </div>
                    </div>
                    <div className="com__content__left__card__row">
                      <div className="com__content__left__card__text black">
                        {files.length}{' '}
                        {files.length === 1 ? 'object' : 'objects'}
                      </div>
                    </div>
                    <div className="com__content__left__card__row">
                      <div className="com__content__left__card__text black">
                        {formatTotal(totalBytes)} used
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          <div
            className="com__content__right"
            onClick={e => {
              if (e.target === e.currentTarget) setSelectedId(null);
            }}
          >
            <div className="com__content__right__card">
              <div className="com__content__right__card__header">Files</div>
              <div className="com__content__right__card__content">
                {loading && files.length === 0 ? (
                  <div className="mf__status">Loading…</div>
                ) : files.length === 0 ? (
                  <div className="mf__status">
                    This folder is empty. Drop files here to upload.
                  </div>
                ) : (
                  files.map(file => (
                    <div
                      key={file.id}
                      className={
                        'com__content__right__card__item' +
                        (selectedId === file.id ? ' mf__item--selected' : '')
                      }
                      onClick={() => setSelectedId(file.id)}
                      onDoubleClick={() => openFile(file)}
                      title={file.display_name}
                    >
                      <img
                        src={iconForMime(file.mime)}
                        alt=""
                        className="com__content__right__card__img"
                      />
                      <div className="com__content__right__card__img-container">
                        <div className="com__content__right__card__text">
                          {file.display_name}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>

        {(error || notice || busy) && (
          <div className="mf__toast">
            {busy ? 'Working…' : error ? error : notice}
          </div>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        multiple
        style={{ display: 'none' }}
        onChange={onPick}
      />
    </Div>
  );
}

function errorMessage(err) {
  if (!(err instanceof ApiError)) return 'Something went wrong.';
  switch (err.code) {
    case 'file_too_large':
      return err.message || 'That file is too large (20 MB max).';
    case 'unsupported_type':
      return 'That file type is not allowed.';
    case 'quota_exceeded':
      return 'Not enough storage quota.';
    case 'csrf_invalid':
      return 'Session expired. Please refresh.';
    case 'unauthorized':
      return 'Please sign in first.';
    case 'not_found':
      return 'File not found.';
    case 'network_error':
      return 'Network error. Try again.';
    default:
      return err.message || 'Something went wrong.';
  }
}

const Div = styled.div`
  height: 100%;
  width: 100%;
  position: absolute;
  display: flex;
  overflow: hidden;
  flex-direction: column;
  background: linear-gradient(to right, #edede5 0%, #ede8cd 100%);

  .mf__empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    gap: 12px;
    font-size: 12px;
    color: #333;
  }
  .mf__empty__icon {
    width: 48px;
    height: 48px;
    opacity: 0.7;
  }
  .mf__empty button {
    padding: 3px 12px;
  }

  .com__toolbar {
    position: relative;
    display: flex;
    align-items: center;
    line-height: 100%;
    height: 24px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.7);
    flex-shrink: 0;
  }
  .com__options {
    height: 23px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.1);
    border-right: 1px solid rgba(0, 0, 0, 0.1);
    padding: 1px 0 1px 2px;
    border-left: 0;
    flex: 1;
  }
  .com__windows-logo {
    height: 100%;
    border-left: 1px solid white;
    border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  }

  .com__function_bar {
    height: 36px;
    display: flex;
    align-items: center;
    font-size: 11px;
    padding: 1px 3px 0;
    border-bottom: 1px solid rgba(0, 0, 0, 0.1);
    flex-shrink: 0;
  }
  .com__function_bar__button {
    display: flex;
    height: 100%;
    align-items: center;
    border: 1px solid rgba(0, 0, 0, 0);
    border-radius: 3px;
    cursor: pointer;
    &:hover {
      border: 1px solid rgba(0, 0, 0, 0.1);
      box-shadow: inset 0 -1px 1px rgba(0, 0, 0, 0.1);
    }
    &:hover:active {
      border: 1px solid rgb(185, 185, 185);
      background-color: #dedede;
      box-shadow: inset 0 -1px 1px rgba(255, 255, 255, 0.7);
      color: rgba(255, 255, 255, 0.7);
      & > * {
        transform: translate(1px, 1px);
      }
    }
  }
  .com__function_bar__button--disable {
    filter: grayscale(1);
    opacity: 0.7;
    display: flex;
    height: 100%;
    align-items: center;
    border: 1px solid rgba(0, 0, 0, 0);
  }
  .com__function_bar__text {
    margin-right: 4px;
  }
  .com__function_bar__icon {
    height: 30px;
    width: 30px;
    &--normalize {
      height: 22px;
      width: 22px;
      margin: 0 4px 0 1px;
    }
    &--margin12 {
      height: 22px;
      width: 22px;
      margin: 0 1px 0 2px;
    }
  }
  .com__function_bar__separate {
    height: 90%;
    width: 1px;
    background-color: rgba(0, 0, 0, 0.2);
    margin: 0 2px;
  }
  .com__function_bar__arrow {
    height: 100%;
    display: flex;
    align-items: center;
    margin: 0 4px;
    &:before {
      content: '';
      display: block;
      border-width: 3px 3px 0;
      border-color: #000 transparent;
      border-style: solid;
    }
  }

  .com__address_bar {
    flex-shrink: 0;
    border-top: 1px solid rgba(255, 255, 255, 0.7);
    height: 20px;
    font-size: 11px;
    display: flex;
    align-items: center;
    padding: 0 2px;
    box-shadow: inset 0 -2px 3px -1px #b0b0b0;
    background: #fff;
  }
  .com__address_bar__title {
    line-height: 100%;
    color: rgba(0, 0, 0, 0.5);
    padding: 5px 5px 5px 3px;
    border-right: 1px solid rgba(0, 0, 0, 0.1);
  }
  .com__address_bar__content {
    height: 100%;
    border: 1px solid rgba(122, 122, 255, 0.6);
    display: flex;
    align-items: center;
    flex: 1;
    margin-left: 3px;
    background: #fff;
    position: relative;
    padding: 0 2px;
  }
  .com__address_bar__content__img {
    width: 14px;
    height: 14px;
  }
  .com__address_bar__content__text {
    flex: 1;
    font-size: 11px;
    color: #000;
    margin: 0 4px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .com__address_bar__go {
    display: flex;
    align-items: center;
    height: 100%;
    padding: 0 12px 0 5px;
    cursor: default;
  }
  .com__address_bar__go__img {
    width: 18px;
    height: 18px;
    margin-right: 3px;
  }

  .com__content {
    flex: 1;
    display: flex;
    overflow: hidden;
    position: relative;
    &--dragover:before {
      content: 'Drop files to upload';
      position: absolute;
      inset: 6px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: rgba(36, 94, 220, 0.08);
      border: 2px dashed #245edc;
      color: #245edc;
      font-size: 14px;
      z-index: 5;
      pointer-events: none;
    }
  }
  .com__content__inner {
    display: flex;
    flex: 1;
    min-height: 0;
    overflow: hidden;
  }
  .com__content__left {
    width: 180px;
    padding: 8px;
    display: flex;
    flex-direction: column;
    gap: 6px;
    background: linear-gradient(to bottom, #7ba0e8 0%, #4c7ad2 100%);
    color: #fff;
    overflow-y: auto;
  }
  .com__content__left__card {
    background: rgba(255, 255, 255, 0.85);
    color: #000;
    border-radius: 3px;
    overflow: hidden;
    box-shadow: 0 1px 0 rgba(0, 0, 0, 0.1);
  }
  .com__content__left__card__header {
    background: linear-gradient(to right, #f0f5ff 0%, #cfd8ea 100%);
    padding: 2px 6px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 11px;
    font-weight: bold;
    color: #123;
  }
  .com__content__left__card__header__img {
    width: 14px;
    height: 14px;
  }
  .com__content__left__card__content {
    padding: 5px 8px 8px;
    display: flex;
    flex-direction: column;
    gap: 3px;
    font-size: 11px;
  }
  .com__content__left__card__row {
    display: flex;
    align-items: center;
    gap: 5px;
    cursor: pointer;
  }
  .com__content__left__card__img {
    width: 14px;
    height: 14px;
  }
  .com__content__left__card__text.link {
    color: #0032c9;
    &:hover {
      text-decoration: underline;
    }
  }
  .com__content__left__card__text.black {
    color: #000;
  }
  .com__content__left__card__text.bold {
    font-weight: bold;
  }
  .mf__row--disabled {
    opacity: 0.5;
    pointer-events: none;
  }

  .com__content__right {
    flex: 1;
    background: #fff;
    padding: 8px;
    overflow-y: auto;
  }
  .com__content__right__card {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
  .com__content__right__card__header {
    font-size: 11px;
    font-weight: bold;
    color: #345;
    border-bottom: 1px solid #d0d7e8;
    padding-bottom: 2px;
  }
  .com__content__right__card__content {
    display: flex;
    flex-wrap: wrap;
    gap: 4px 12px;
    padding: 6px 0;
  }
  .com__content__right__card__item {
    width: 96px;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 4px;
    border: 1px dotted transparent;
    cursor: default;
    text-align: center;
    user-select: none;
  }
  .com__content__right__card__item:hover {
    background: rgba(36, 94, 220, 0.08);
  }
  .com__content__right__card__img {
    width: 32px;
    height: 32px;
  }
  .com__content__right__card__img-container {
    margin-top: 3px;
    max-width: 100%;
  }
  .com__content__right__card__text {
    font-size: 11px;
    color: #000;
    overflow-wrap: anywhere;
    line-height: 1.2;
  }
  .mf__item--selected {
    background: rgba(36, 94, 220, 0.18) !important;
    border: 1px dotted #245edc !important;
  }
  .mf__item--selected .com__content__right__card__text {
    background: #316ac5;
    color: #fff;
    padding: 0 2px;
  }
  .mf__status {
    padding: 24px 12px;
    color: #555;
    font-size: 12px;
  }
  .mf__toast {
    position: absolute;
    bottom: 6px;
    right: 6px;
    background: #fff;
    border: 1px solid #b6b6b6;
    padding: 3px 8px;
    font-size: 11px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    z-index: 6;
  }
`;

export default MyFolder;
